import React from "react";
import { Fab, Zoom } from "@mui/material";
import { KeyboardArrowUp } from "@mui/icons-material";
import { gradients } from "../utils/theme";
import { useScrollToTop } from "../hooks/useScrollToTop";

/**
 * ScrollToTopFab Component
 *
 * Self-contained floating action button that appears when user scrolls past a threshold
 * and smoothly scrolls back to the top when clicked.
 *
 * **Features:**
 * - Automatic show/hide based on scroll position (400px threshold)
 * - Enterprise-grade navy gradient styling matching brand colors
 * - Smooth zoom animation on appear/disappear
 * - Responsive positioning (16px mobile, 24px desktop)
 * - Hover effects with scale and translateY transforms
 * - Accessibility: aria-label for screen readers
 * - No props required - fully self-contained
 *
 * **Usage:**
 * ```tsx
 * const MyScreen = () => {
 *   return (
 *     <>
 *       {/* Screen content *\/}
 *       <ScrollToTopFab />
 *     </>
 *   );
 * };
 * ```
 */

const ScrollToTopFab: React.FC = () => {
  const { showScrollTop, scrollToTop } = useScrollToTop(400);

  return (
    <Zoom in={showScrollTop} unmountOnExit>
      <Fab
        onClick={scrollToTop}
        aria-label="scroll back to top"
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 }, // Responsive positioning
          right: { xs: 16, sm: 24 },
          background: gradients.navyLux, // MallInHand navy gradient
          color: "white", // White text for contrast
          boxShadow: "0 8px 24px rgba(26, 35, 126, 0.4)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smooth transitions
          zIndex: 1000, // Ensure it's above other content
          "&:hover": {
            background: "linear-gradient(135deg, #0d1b5e 0%, #1e2875 100%)", // Darker navy on hover
            boxShadow: "0 12px 32px rgba(26, 35, 126, 0.6)", // Enhanced shadow
            transform: "translateY(-4px) scale(1.05)", // Lift effect
          },
          "&:active": {
            transform: "translateY(-2px) scale(1.02)", // Pressed state
          },
        }}
      >
        <KeyboardArrowUp sx={{ fontSize: 28 }} />
      </Fab>
    </Zoom>
  );
};

export default React.memo(ScrollToTopFab);
